import { fetchMapFanToken, clearMapFanToken } from '../mapfan/auth';
import * as Sentry from "@sentry/react";

type resType = 'error' | 'success' | 'warning';

type MapFanAddressResponse = {
  status: string;
  hit: number;
  results: Array<{ name: string; lon: number; lat: number }>;
  level: number;
};

const levelDescriptions: {
  [key: number]: { type: resType, match: string };
} = {
  0: { type: 'warning', match: '都道府県' },
  1: { type: 'warning', match: '市区町村' },
  2: { type: 'warning', match: '条町丁目' },
  3: { type: 'success', match: '番地' },
  4: { type: 'success', match: '号' },
};

type geocorderResType = {
  point: { lng: number; lat: number };
  message: string;
  type: resType;
};

const BASE_ADDRESS = '静岡県焼津市';

const MAPFAN_TIMEOUT_MS = 10000;

/** APIレスポンスが MapFanAddressResponse の構造を持つか検証する型ガード */
const isMapFanAddressResponse = (raw: unknown): raw is MapFanAddressResponse =>
  typeof raw === 'object' &&
  raw !== null &&
  typeof (raw as Record<string, unknown>).status === 'string' &&
  typeof (raw as Record<string, unknown>).hit === 'number' &&
  Array.isArray((raw as Record<string, unknown>).results) &&
  typeof (raw as Record<string, unknown>).level === 'number';

/** results[0] が有効な座標・名称を持つか検証する型ガード */
const isValidResult = (result: unknown): result is { name: string; lon: number; lat: number } =>
  typeof result === 'object' &&
  result !== null &&
  typeof (result as Record<string, unknown>).name === 'string' &&
  typeof (result as Record<string, unknown>).lon === 'number' &&
  typeof (result as Record<string, unknown>).lat === 'number';

/** APIレスポンスのエラー種別を返す。エラーでなければ null */
const getMapFanError = (raw: unknown): 'auth' | 'api' | null => {
  if (typeof raw !== 'object' || raw === null) return 'api';
  const status = (raw as Record<string, unknown>).status;
  if (typeof status !== 'string') return 'api';
  if (status.includes('E02003')) return 'auth';
  if (status !== 'success') return 'api';
  return null;
};

const callMapFanAddr = async (addr: string, token: string): Promise<unknown> => {
  const url = `https://api-srch.mapfan.com/v1/addr?key=${encodeURIComponent(token)}&addr=${encodeURIComponent(addr)}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), MAPFAN_TIMEOUT_MS);
  try {
    const res = await fetch(url, { signal: controller.signal });
    return res.json();
  } finally {
    clearTimeout(timeoutId);
  }
};

export const searchAddress = async (searchTerm: string): Promise<geocorderResType | undefined> => {
  let mapFanToken = await fetchMapFanToken();

  const addr = (() => {
    // BASE_ADDRESSの末尾からsearchTermの先頭との最長一致サフィックスを探す
    for (let i = BASE_ADDRESS.length; i >= 1; i--) {
      if (searchTerm.startsWith(BASE_ADDRESS.slice(BASE_ADDRESS.length - i))) {
        return BASE_ADDRESS.slice(0, BASE_ADDRESS.length - i) + searchTerm;
      }
    }
    return `${BASE_ADDRESS}${searchTerm}`;
  })();

  let raw: unknown = await callMapFanAddr(addr, mapFanToken);

  // E02003: トークン期限切れ → キャッシュ破棄して再認証し1回だけリトライ
  if (getMapFanError(raw) === 'auth') {
    clearMapFanToken();
    mapFanToken = await fetchMapFanToken();
    raw = await callMapFanAddr(addr, mapFanToken);
    if (getMapFanError(raw) !== null) {
      const status = isMapFanAddressResponse(raw) ? raw.status : 'unknown';
      throw new Error(`MapFan API 再認証後もエラーが発生しました: ${status}`);
    }
  }

  if (!isMapFanAddressResponse(raw)) {
    const error = new Error('MapFan API レスポンスの形式が不正です');
    Sentry.captureException(error);
    throw error;
  }

  const data = raw;

  if (data.status !== 'success') {
    const error = new Error(`MapFan API エラー: ${data.status}`);
    Sentry.captureException(error);
    throw error;
  }

  if (data.hit === 0 || !data.results.length) {
    return undefined;
  }

  const levelInfo = levelDescriptions[data.level];
  if (!levelInfo) {
    return undefined;
  }

  if (!isValidResult(data.results[0])) {
    return undefined;
  }

  const resultName = data.results[0].name;

  if (resultName === BASE_ADDRESS && addr !== BASE_ADDRESS) {
    return undefined;
  }

  const simpleAddress =
    data.level >= 2 && resultName.startsWith(BASE_ADDRESS)
      ? resultName.slice(BASE_ADDRESS.length)
      : resultName;

  return {
    point: { lng: data.results[0].lon, lat: data.results[0].lat },
    message: `入力した住所の${levelInfo.match}まで判別ができました。${simpleAddress}${levelInfo.type !== 'success' ? 'の代表値' : ''}に移動します。`,
    type: levelInfo.type,
  };
};
